interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
  version: number;
}

const CACHE_VERSION = 5; // Bumped to fix Supabase 1000-row limit issue in predictions
const STORAGE_PREFIX = 'footy_cache_';

class QueryCache {
  private cache: Map<string, CacheEntry<any>>;
  private inFlightRequests: Map<string, Promise<any>>;
  private defaultTTL: number;
  private persistToStorage: boolean;

  constructor(defaultTTL: number = 30000, persistToStorage: boolean = true) {
    this.cache = new Map();
    this.inFlightRequests = new Map();
    this.defaultTTL = defaultTTL;
    this.persistToStorage = persistToStorage;

    // Load cache from localStorage on initialization
    this.loadFromStorage();
  }

  generateKey(prefix: string, params: Record<string, any>): string {
    const sortedParams = Object.keys(params)
      .filter(key => params[key] !== null && params[key] !== undefined)
      .sort()
      .map(key => `${key}:${params[key]}`)
      .join('|');
    return `${prefix}:${sortedParams}`;
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) {
      // Try to load from localStorage if not in memory
      const stored = this.getFromStorage<T>(key);
      if (stored) {
        this.cache.set(key, stored);
        return stored.data;
      }
      return null;
    }

    const now = Date.now();
    if (now > entry.expiresAt) {
      this.cache.delete(key);
      this.removeFromStorage(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Stale-while-revalidate read: returns cached data even if expired,
   * along with an `isStale` flag so callers can trigger background revalidation.
   * Honours a `maxStaleAge` (default 24h) — beyond that, returns null.
   */
  getStale<T>(key: string, maxStaleAge: number = 24 * 60 * 60 * 1000): { data: T; isStale: boolean } | null {
    let entry = this.cache.get(key) as CacheEntry<T> | undefined;
    if (!entry) {
      const stored = this.getFromStorageRaw<T>(key);
      if (stored) {
        this.cache.set(key, stored);
        entry = stored;
      }
    }
    if (!entry) return null;

    const now = Date.now();
    const isStale = now > entry.expiresAt;
    if (isStale && now - entry.timestamp > maxStaleAge) {
      this.cache.delete(key);
      this.removeFromStorage(key);
      return null;
    }
    return { data: entry.data, isStale };
  }

  /**
   * SWR fetch: invokes onData with cached value (if any) immediately,
   * then revalidates in the background and calls onData again with fresh data.
   * Returns the eventual fresh-fetch promise.
   */
  async swr<T>(
    key: string,
    fetchFn: () => Promise<T>,
    onData: (data: T, isFresh: boolean) => void,
    ttl?: number,
    maxStaleAge?: number
  ): Promise<T> {
    const stale = this.getStale<T>(key, maxStaleAge);
    if (stale) {
      onData(stale.data, !stale.isStale);
      if (!stale.isStale) {
        return stale.data;
      }
    }
    const fresh = await this.deduplicate(key, fetchFn, ttl);
    onData(fresh, true);
    return fresh;
  }

  set<T>(key: string, data: T, ttl?: number): void {
    const now = Date.now();
    const expiresAt = now + (ttl || this.defaultTTL);

    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      expiresAt,
      version: CACHE_VERSION
    };

    this.cache.set(key, entry);

    // Persist to localStorage for longer-lived cache
    if (this.persistToStorage) {
      this.saveToStorage(key, entry);
    }
  }

  async deduplicate<T>(
    key: string,
    fetchFn: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    const inFlight = this.inFlightRequests.get(key);
    if (inFlight) {
      return inFlight;
    }

    const promise = fetchFn()
      .then(data => {
        if (data !== null && data !== undefined) {
          this.set(key, data, ttl);
        }
        this.inFlightRequests.delete(key);
        return data;
      })
      .catch(error => {
        this.inFlightRequests.delete(key);
        throw error;
      });

    this.inFlightRequests.set(key, promise);
    return promise;
  }

  invalidate(pattern?: string): void {
    if (!pattern) {
      this.cache.clear();
      this.clearStorage();
      return;
    }

    const keysToDelete: string[] = [];
    for (const key of this.cache.keys()) {
      if (key.startsWith(pattern)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.removeFromStorage(key);
    });
  }

  invalidateByParams(prefix: string, params: Record<string, any>): void {
    const pattern = `${prefix}:`;
    const keysToDelete = new Set<string>();

    // Check in-memory cache
    for (const key of this.cache.keys()) {
      if (key.startsWith(pattern)) {
        const matchesParams = Object.entries(params).every(([paramKey, paramValue]) => {
          return key.includes(`${paramKey}:${paramValue}`);
        });
        if (matchesParams) {
          keysToDelete.add(key);
        }
      }
    }

    // Also check localStorage for matching keys
    if (typeof localStorage !== 'undefined') {
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const storageKey = localStorage.key(i);
          if (storageKey?.startsWith(STORAGE_PREFIX)) {
            const cacheKey = storageKey.substring(STORAGE_PREFIX.length);
            if (cacheKey.startsWith(pattern)) {
              const matchesParams = Object.entries(params).every(([paramKey, paramValue]) => {
                return cacheKey.includes(`${paramKey}:${paramValue}`);
              });
              if (matchesParams) {
                keysToDelete.add(cacheKey);
              }
            }
          }
        }
      } catch (error) {
        console.warn('Failed to check localStorage keys:', error);
      }
    }

    // Delete from both memory and storage
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.removeFromStorage(key);
    });
  }

  clear(): void {
    this.cache.clear();
    this.inFlightRequests.clear();
    this.clearStorage();
  }

  private saveToStorage<T>(key: string, entry: CacheEntry<T>): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const storageKey = STORAGE_PREFIX + key;
      localStorage.setItem(storageKey, JSON.stringify(entry));
    } catch (error) {
      // Storage quota exceeded or unavailable - fail silently
      console.warn('Failed to save to localStorage:', error);
    }
  }

  private getFromStorage<T>(key: string): CacheEntry<T> | null {
    const entry = this.getFromStorageRaw<T>(key);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.removeFromStorage(key);
      return null;
    }
    return entry;
  }

  /** Reads raw entry from storage (does not enforce expiry — used for SWR). */
  private getFromStorageRaw<T>(key: string): CacheEntry<T> | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const storageKey = STORAGE_PREFIX + key;
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      const entry: CacheEntry<T> = JSON.parse(stored);
      if (entry.version !== CACHE_VERSION) {
        this.removeFromStorage(key);
        return null;
      }
      return entry;
    } catch (error) {
      console.warn('Failed to load from localStorage:', error);
      return null;
    }
  }

  private removeFromStorage(key: string): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const storageKey = STORAGE_PREFIX + key;
      localStorage.removeItem(storageKey);
    } catch (error) {
      // Fail silently
    }
  }

  private clearStorage(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(STORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      // Fail silently
    }
  }

  private loadFromStorage(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const keys = Object.keys(localStorage);
      keys.forEach(storageKey => {
        if (storageKey.startsWith(STORAGE_PREFIX)) {
          const key = storageKey.substring(STORAGE_PREFIX.length);
          const entry = this.getFromStorage(key);
          if (entry) {
            this.cache.set(key, entry);
          }
        }
      });
    } catch (error) {
      // Fail silently
    }
  }

  getStats() {
    return {
      cacheSize: this.cache.size,
      inFlightRequests: this.inFlightRequests.size,
      entries: Array.from(this.cache.entries()).map(([key, entry]) => ({
        key,
        age: Date.now() - entry.timestamp,
        ttl: entry.expiresAt - Date.now()
      }))
    };
  }
}

export const queryCache = new QueryCache(30000, true);

export const CACHE_TTL = {
  SHORT: 30000,          // 30 seconds - for user-specific data that changes frequently
  DEFAULT: 120000,       // 2 minutes - for leaderboards and status
  MEDIUM: 300000,        // 5 minutes - for fixtures and games
  LONG: 1800000,         // 30 minutes - for organizations and competitions (rarely change)
  REFERENCE: 3600000     // 60 minutes - for truly static reference data
};
